import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Grid2x2, Loader2, Monitor, Smartphone, Sparkles } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@meno/ui/components/ui/dialog'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'
import { Textarea } from '@meno/ui/components/ui/textarea'
import {
  TEMPLATES, type LayoutTemplate, type TemplateDef,
  buildEmptyZones, gridTracks, zoneStyle, getTemplate,
  pickerTemplates, templateOrientation,
} from '@/lib/layoutTemplates'
import CompanionSection from '@/components/CompanionSection'
import { companionsFromEntity, type Companions, type CompanionFields } from '@/components/companionsModel'
import { NavItem, NavRail } from '@/components/DialogNavRail'

// LayoutDialog — create / edit form for a SignageLayout. Same chrome as
// PlaylistDialog (size, header, name/description row, left nav rail),
// adapted to a layout's nature: "Content" here means picking a template
// (a layout has no items of its own to select — that's per-zone, on the
// detail page), and "Companions" is the same audio/transition section
// Playlist/Layout creation share.
type NavKey = 'content' | 'companions'

export type LayoutDialogProps = {
  layout: ({ id: number; name: string; description?: string; template: string } & CompanionFields) | null
  onSave: (data: {
    name: string
    description: string
    template: LayoutTemplate
    zones?: ReturnType<typeof buildEmptyZones>
    companions: Companions
  }) => Promise<void>
  onClose: () => void
}

// Generates the create-flow default name when the user hasn't typed one.
// We never block the Create button on an empty name — a layout without a
// human label is still a valid record, and forcing one was the most common
// "I can't create" complaint. The detail page lets the user rename later.
// This can land as the actual saved name (not just a placeholder), so the
// wrapper words route through i18n — the template label itself is still an
// English config string (TEMPLATES), a separate, larger translation task.
function defaultLayoutName(template: LayoutTemplate, t: (key: string, opts: Record<string, unknown>) => string): string {
  const label = TEMPLATES.find(tpl => tpl.key === template)?.label ?? 'Layout'
  return t('layout.defaultName', { defaultValue: 'New {{label}} Layout', label })
}

export default function LayoutDialog({ layout, onSave, onClose }: LayoutDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(layout?.name ?? '')
  const [description, setDescription] = useState(layout?.description ?? '')
  const [template, setTemplate] = useState<LayoutTemplate>(
    (layout?.template as LayoutTemplate) ?? 'default',
  )
  const [saving, setSaving] = useState(false)
  const [activeNav, setActiveNav] = useState<NavKey>('content')
  // Publish companions — audio / stop screen / transition co-published with
  // the layout. Seeded from the row on edit so picks survive round-trips.
  const [companions, setCompanions] = useState<Companions>(companionsFromEntity(layout))
  // Surface the most recent save error inline so the operator can see why
  // the dialog didn't close, instead of having to read a fleeting toast.
  const [saveError, setSaveError] = useState<string | null>(null)
  const hasCompanions = companions.audioId != null || companions.audioPlaylistId != null || companions.transition != null

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    setSaveError(null)
    const finalName = name.trim() || (layout?.name ?? defaultLayoutName(template, t))
    try {
      // On create, seed zones from the template so the detail page lands
      // with an empty Zone 1 / Zone 2 / etc. ready to receive content. On
      // edit we leave zones alone so existing content survives a template
      // change of the same arity (caller can still re-seed if needed).
      const payload = layout
        ? { name: finalName, description: description.trim(), template, companions }
        : { name: finalName, description: description.trim(), template, zones: buildEmptyZones(template), companions }
      await onSave(payload)
    } catch (err: any) {
      // Surface the actual reason from the API so the user knows what to
      // fix. Falls back to a generic message if the error shape is unknown.
      const detail = err?.response?.data?.detail
      setSaveError(
        typeof detail === 'string'
          ? detail
          : t('layout.createFailed', 'Failed to create layout'),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open && !saving) onClose() }}>
      <DialogContent className="max-w-5xl flex flex-col max-h-[92vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle>
            {layout ? t('layout.edit', 'Edit Layout') : t('layout.new', 'New Layout')}
          </DialogTitle>
        </DialogHeader>

        {/* Name + description — name is no longer required at the dialog
            level so an operator who just wants a quick layout can skip
            straight to template selection. The detail page exposes the
            rename pencil for refining it later. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-4 border-b border-border shrink-0">
          <div className="grid gap-1.5">
            <Label htmlFor="layout-name">{t('layout.name', 'Layout name')}</Label>
            <Input
              id="layout-name"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={150}
              placeholder={defaultLayoutName(template, t)}
              autoFocus
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="layout-description">{t('common.description', 'Description')}</Label>
            <Textarea
              id="layout-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              placeholder={t('common.optionalDescription', 'Optional description...')}
              className="resize-none"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex">
          {/* Left nav rail — same shape as PlaylistDialog's: Content is the
              layout's "what to publish" decision (here, a template — a
              layout has no items of its own, that's per-zone on the detail
              page); Companions is the same audio/transition section. */}
          <NavRail>
            <NavItem
              active={activeNav === 'content'}
              icon={Grid2x2}
              label={t('playlist.navContent', 'Content')}
              onClick={() => setActiveNav('content')}
            />
            <NavItem
              active={activeNav === 'companions'}
              icon={Sparkles}
              label={t('playlist.navCompanions', 'Companions')}
              dot={hasCompanions}
              onClick={() => setActiveNav('companions')}
            />
          </NavRail>

          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {/* Both panels stay mounted (hidden via CSS), matching
                PlaylistDialog, so picks aren't lost switching tabs. */}
            <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 ${activeNav === 'content' ? '' : 'hidden'}`}>
              <LayoutPickerGrid value={template} onChange={setTemplate} />
            </div>
            <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 ${activeNav === 'companions' ? '' : 'hidden'}`}>
              <CompanionSection value={companions} onChange={setCompanions} />
            </div>
          </div>
        </div>

        {/* Persistent (not tab-scoped) so an error surfaces regardless of
            which tab the operator is looking at when Save fails. */}
        {saveError && (
          <p className="text-sm text-destructive px-6 py-2 border-t border-border shrink-0">{saveError}</p>
        )}

        <DialogFooter className="gap-2 px-6 py-3 border-t border-border">
          <Button variant="outline" disabled={saving} onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={saving} onClick={handleSave} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {layout ? t('common.save', 'Save') : t('common.create', 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── LayoutPickerGrid ─────────────────────────────────────────────────────
// Exposed so the "Change Layout" dialog on LayoutDetailsPage can reuse
// the exact same picker UI (consistent visual vocabulary between create
// and edit flows). The thumbnails are themselves rendered with the same
// `gridTracks` + `zoneStyle` helpers used at runtime, so what the user
// sees in the picker is exactly the structure they'll get on the
// preview frame.
export function LayoutPickerGrid({
  value, onChange,
}: {
  value: LayoutTemplate
  onChange: (next: LayoutTemplate) => void
}) {
  const { t } = useTranslation()
  const zoneCount = getTemplate(value).zones.length
  // Filter templates by the screen orientation they suit. Default to the
  // orientation of the current selection ('both' → Horizontal) so editing a
  // vertical layout opens on the Vertical tab.
  const initialOrient = templateOrientation(value)
  const [orient, setOrient] = useState<'horizontal' | 'vertical'>(
    initialOrient === 'vertical' ? 'vertical' : 'horizontal',
  )

  // Pickable templates for this orientation (corner/side + >9-zone grids are
  // already excluded by pickerTemplates). 'both' templates show in either tab.
  const visible = pickerTemplates().filter(tpl => {
    const o = templateOrientation(tpl.key)
    return o === 'both' || o === orient
  })
  // Always surface the currently-selected template even if it wouldn't
  // otherwise appear in this tab (e.g. editing a hidden/legacy layout).
  if (!visible.some(tpl => tpl.key === value)) {
    const active = TEMPLATES.find(tpl => tpl.key === value)
    if (active) visible.unshift(active)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Label className="text-sm font-semibold text-foreground shrink-0">
          {t('layout.chooseLayout', 'Choose Layout')}
        </Label>
        {/* Orientation filter — Horizontal (landscape) / Vertical (portrait). */}
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5">
          <OrientTab active={orient === 'horizontal'} onClick={() => setOrient('horizontal')} icon={Monitor} label={t('layout.orientHorizontal', 'Horizontal')} />
          <OrientTab active={orient === 'vertical'} onClick={() => setOrient('vertical')} icon={Smartphone} label={t('layout.orientVertical', 'Vertical')} />
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0 ms-auto">
          {t('layout.zoneCountValue', '{{count}} zones', { count: zoneCount })}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {visible.map(tpl => (
          <LayoutThumb
            key={tpl.key}
            template={tpl}
            vertical={orient === 'vertical'}
            active={value === tpl.key}
            onClick={() => onChange(tpl.key)}
          />
        ))}
      </div>
    </div>
  )
}

// Segmented-control button for the orientation filter.
function OrientTab({
  active, onClick, icon: Icon, label,
}: {
  active: boolean
  onClick: () => void
  icon: typeof Monitor
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`px-2.5 h-7 text-xs font-medium rounded-md inline-flex items-center gap-1.5 transition-colors ${
        active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  )
}

// One picker tile — clickable button that shows a 16:9 thumbnail of the
// layout above its label. Mirrors the dark-on-light scheme from the
// reference screenshots: the outer card stays neutral while the inner
// frame goes dark so the zone rectangles read like a screen preview.
function LayoutThumb({
  template, active, onClick, vertical = false,
}: {
  template: TemplateDef
  active: boolean
  onClick: () => void
  // Render the preview frame portrait (9:16) so a Vertical-tab template reads
  // the way it'll appear on a portrait screen.
  vertical?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`group flex flex-col items-center gap-2 p-2 rounded-xl border-2 transition-all text-center ${
        active
          ? 'border-amber-500 bg-amber-50/60 dark:bg-amber-500/10 shadow-sm'
          : 'border-transparent hover:border-amber-200 dark:hover:border-amber-500/30'
      }`}
    >
      {/* Pinned LTR: a diagram of the physical zone layout the TV will
          actually render — CSS Grid mirrors under dir="rtl" (Arabic), which
          would show a different template shape here than what plays. Same
          fix as ZonePreviewFrame / the add-screen wizard's browser mock. */}
      <div dir="ltr" className={`${vertical ? 'aspect-[9/16] w-1/2' : 'aspect-video w-full'} rounded-lg bg-slate-900 dark:bg-slate-950 p-2 ring-1 ring-black/10 dark:ring-white/10`}>
        <div
          className="w-full h-full"
          style={{
            display: 'grid',
            ...gridTracks(template),
            gap: '4px',
          }}
        >
          {template.zones.map((zone, i) => (
            <div
              key={i}
              style={zoneStyle(zone)}
              className={`rounded-sm ${active ? 'bg-slate-400/80' : 'bg-slate-500/80 group-hover:bg-slate-400/80'} transition-colors`}
            />
          ))}
        </div>
      </div>
      <span className={`text-xs font-medium ${active ? 'text-amber-700 dark:text-amber-300' : 'text-foreground'}`}>
        {template.label}
      </span>
    </button>
  )
}
